import React from 'react';
import { HeartCrack, AlertTriangle, TrendingUp, Clock, Users } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v);

const ITEMS = [
  {
    key: 'deceasedCostYTD',
    label: 'Deceased Costs YTD',
    format: 'currency',
    icon: HeartCrack,
    stripe: 'bg-rose-500',
    bg: 'bg-rose-50',
    border: 'border-rose-100',
    text: 'text-rose-700',
    iconBg: 'bg-rose-100',
  },
  {
    key: 'outstanding',
    label: 'Outstanding',
    format: 'special',
    icon: AlertTriangle,
    stripe: 'bg-amber-500',
    bg: 'bg-amber-50',
    border: 'border-amber-100',
    text: 'text-amber-700',
    iconBg: 'bg-amber-100',
  },
  {
    key: 'avgContribution',
    label: 'Avg Contribution',
    format: 'currency',
    icon: TrendingUp,
    stripe: 'bg-emerald-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    text: 'text-emerald-700',
    iconBg: 'bg-emerald-100',
  },
  {
    key: 'pendingApplications',
    label: 'Pending Applications',
    format: 'number',
    icon: Clock,
    stripe: 'bg-blue-500',
    bg: 'bg-blue-50',
    border: 'border-blue-100',
    text: 'text-blue-700',
    iconBg: 'bg-blue-100',
  },
  {
    key: 'totalFamilies',
    label: 'Total Families',
    format: 'number',
    icon: Users,
    stripe: 'bg-purple-500',
    bg: 'bg-purple-50',
    border: 'border-purple-100',
    text: 'text-purple-700',
    iconBg: 'bg-purple-100',
  },
];

export default function SecondaryMetrics({ stats }) {
  const outstandingPct = stats.totalMembers > 0
    ? Math.min(Math.round((stats.outstandingCount / stats.totalMembers) * 100), 100)
    : 0;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {ITEMS.map(({ key, label, format, icon: Icon, stripe, bg, border, text, iconBg }) => {
        let display, sub, progressPct;

        if (key === 'outstanding') {
          display = (stats.outstandingCount || 0).toLocaleString();
          sub = fmt(stats.outstandingAmount || 0);
          progressPct = outstandingPct;
        } else if (format === 'currency') {
          display = fmt(stats[key] || 0);
        } else {
          display = (stats[key] || 0).toLocaleString();
        }

        return (
          <div
            key={key}
            className={`relative rounded-xl ${bg} border ${border} overflow-hidden hover:shadow-md transition-all duration-200 group hover:-translate-y-0.5`}
          >
            {/* Left color stripe */}
            <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${stripe}`} />

            <div className="pl-4 pr-3 py-3.5">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-6 h-6 rounded-md ${iconBg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-3.5 h-3.5 ${text}`} />
                </div>
                <p className={`text-[10px] font-bold uppercase tracking-wider ${text} opacity-70 leading-tight`}>{label}</p>
              </div>
              <p className={`text-xl font-black ${text} tabular-nums leading-none`}>{display}</p>
              {sub && <p className="text-[11px] text-slate-400 mt-0.5 tabular-nums font-medium">{sub}</p>}
              {progressPct !== undefined && (
                <div className="mt-2">
                  <div className="h-1.5 bg-white/80 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${stripe} opacity-80 rounded-full transition-all duration-700`}
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <p className={`text-[10px] ${text} opacity-50 mt-0.5`}>{progressPct}% of members</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
